import { Printer } from "lucide-react";

export const ASSETS = {
  logoDark: "/nk-logo-dark.png",
  logoLight: "/nk-logo-light.png",
  beaded: "https://customer-assets-rejwkqb3.emergentagent.net/job_c2ace06b-eb95-4eb9-871b-f8783f8db404/artifacts/fzrzp4ky_normal_2024D_A7406913.webp",
  cathedral: "https://customer-assets-jai6qajn.emergentagent.net/job_bridal-australia-hub/artifacts/dmv1urri_normal_2024D_A7406941.webp",
  lace: "https://customer-assets-jai6qajn.emergentagent.net/job_bridal-australia-hub/artifacts/fvima9a4_normal_2024D_A7406957.webp",
  consult: "https://customer-assets-jai6qajn.emergentagent.net/job_bridal-australia-hub/artifacts/474fyrd5_hf_20260817_034623_fed093b0-8622-4d34-bf50-d1a5751125e9.png",
  sew: "https://customer-assets-jai6qajn.emergentagent.net/job_bridal-australia-hub/artifacts/ol8z477u_hf_20260817_032014_2c6e8265-940e-498e-badf-36180ab032a5.png",
  couple: "https://customer-assets-jai6qajn.emergentagent.net/job_bridal-australia-hub/artifacts/97njgpb8_normal_2024D_A7406605.webp",
  satin: "https://customer-assets-jai6qajn.emergentagent.net/job_bridal-australia-hub/artifacts/dkm0wa8w_normal_2024D_A7406893.webp",
  altMeasure: "https://customer-assets-jai6qajn.emergentagent.net/job_bridal-australia-hub/artifacts/hhr380mn_hf_20260820_081044_1fb20925-ef6d-41f8-bedf-36f2e45af519%20%281%29.png",
  altHem: "https://customer-assets-jai6qajn.emergentagent.net/job_bridal-australia-hub/artifacts/lzp200gw_hf_20260817_063341_824e6803-3e3f-45a1-808e-164eb74ba6ed.png",
  handStitch: "https://customer-assets-jai6qajn.emergentagent.net/job_bridal-australia-hub/artifacts/zaopw71t_hf_20260829_072117_45407b17-63ba-45de-8b10-1fa587720d48.png",
};

const mm = (v) => `${v}mm`;
const LINE = "#111";

function CropMarks({ totalW, totalH, inset, trimW, trimH, mark }) {
  const v = { position: "absolute", width: "0.25mm", background: LINE, height: mm(mark) };
  const h = { position: "absolute", height: "0.25mm", background: LINE, width: mm(mark) };
  const xL = inset, xR = inset + trimW, yT = inset, yB = inset + trimH;
  return (
    <>
      {/* verticals */}
      <div style={{ ...v, left: mm(xL), top: 0 }} />
      <div style={{ ...v, left: mm(xL), top: mm(totalH - mark) }} />
      <div style={{ ...v, left: mm(xR), top: 0 }} />
      <div style={{ ...v, left: mm(xR), top: mm(totalH - mark) }} />
      {/* horizontals */}
      <div style={{ ...h, top: mm(yT), left: 0 }} />
      <div style={{ ...h, top: mm(yT), left: mm(totalW - mark) }} />
      <div style={{ ...h, top: mm(yB), left: 0 }} />
      <div style={{ ...h, top: mm(yB), left: mm(totalW - mark) }} />
    </>
  );
}

export function Artboard({ trimW, trimH, bleed = 3, mark = 5, folds = [], bg = "#FDFBF7", children, testid }) {
  const inset = bleed + mark;
  const totalW = trimW + 2 * inset;
  const totalH = trimH + 2 * inset;
  return (
    <div className="artboard" data-testid={testid} style={{ width: mm(totalW), height: mm(totalH), position: "relative", background: "#fff" }}>
      <div style={{ position: "absolute", left: mm(mark), top: mm(mark), width: mm(trimW + 2 * bleed), height: mm(trimH + 2 * bleed), overflow: "hidden", background: bg }}>
        {children}
      </div>
      <CropMarks totalW={totalW} totalH={totalH} inset={inset} trimW={trimW} trimH={trimH} mark={mark} />
      {folds.map((fx, i) => (
        <div key={i}>
          <div style={{ position: "absolute", left: mm(inset + fx), top: 0, height: mm(mark - 1), borderLeft: `0.25mm dashed ${LINE}` }} />
          <div style={{ position: "absolute", left: mm(inset + fx), top: mm(totalH - (mark - 1)), height: mm(mark - 1), borderLeft: `0.25mm dashed ${LINE}` }} />
        </div>
      ))}
    </div>
  );
}

export function PrintPage({ pageW, pageH, title, subtitle, note, children }) {
  return (
    <div className="print-root">
      <style>{`
        @media print {
          @page { size: ${pageW}mm ${pageH}mm; margin: 0; }
          html, body { margin: 0 !important; background: #fff !important; }
          .no-print { display: none !important; }
          .artboard { box-shadow: none !important; margin: 0 !important; break-after: page; }
          .artboard:last-of-type { break-after: auto; }
        }
        * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .print-root { background: #c9c3ba; min-height: 100vh; padding: 28px 16px 64px; display: flex; flex-direction: column; align-items: center; gap: 30px; font-family: 'Manrope', sans-serif; }
        .artboard { box-shadow: 0 24px 60px rgba(0,0,0,.28); }
        .pk-grain::after { content:""; position:absolute; inset:0; pointer-events:none; opacity:.045;
          background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E"); }
      `}</style>
      <div className="no-print w-full max-w-[820px] flex items-center justify-between text-ink">
        <div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-ink/50">NK Bridal · Print artwork</p>
          <p className="font-serif text-xl text-ink">{title}</p>
          {subtitle && <p className="text-[12px] text-ink/70 mt-1">{subtitle}</p>}
        </div>
        <button onClick={() => window.print()} data-testid="print-button" className="inline-flex items-center gap-2 bg-ink text-ivory px-6 py-3 text-xs uppercase tracking-[0.25em] hover:opacity-90">
          <Printer size={15} /> Print / Save PDF
        </button>
      </div>
      {note && <p className="no-print -mt-4 text-[11px] text-ink/70 max-w-[820px] text-center">{note}</p>}
      {children}
    </div>
  );
}
